import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import lodashWithout from 'lodash/fp/without';
import lodashUniq from 'lodash/fp/uniq';
import lodashGroupBy from 'lodash/fp/groupBy';
import lodashKeyBy from 'lodash/fp/keyBy';

import typeCheck from 'common/util/typeCheck';
import { UTILITY_PROVIDERS_MILES_RANGE } from 'common/utility/constants';
import { SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING } from 'api/utilityActionTypes';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import UtilityProviderActions from 'client/actions/UtilityProvider';
import { getRangeIdentifier } from 'client/actions/UtilityProvider/getMappedProvidersInRange';

import useDispatch from 'client/hooks/useDispatch';
import useBranch from 'client/hooks/useBranch';

import SrUtilityProfitAdminProcessAddressForm from 'client/components/SrUtilityProfitAdminProcessAddressPage/SrUtilityProfitAdminProcessAddressForm';
import UPProviderSearchForm from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';

const UPAdminProcessAddressFormConnected = ({ addressId, onUpdate, markAsRequiredEnabled, addressRequiredUtilityTypes }) => {
  const dispatch = useDispatch();

  const { addressCoverage, utilityTypes, utilityProvidersByAddress } = useBranch({
    addressCoverage: ['utilityProviders', 'coverageByAddressId', addressId],
    utilityTypes: ['utilityActivations', 'types'],
    utilityProvidersByAddress: ['utilityProviders', 'byAddressId', addressId],
  });

  const { utilityProvidersInRange } = useBranch({
    ...(!addressCoverage
      ? {}
      : {
          utilityProvidersInRange: [
            'utilityProviders',
            'coverageByRange',
            getRangeIdentifier({
              latitude: addressCoverage.latitude,
              longitude: addressCoverage.longitude,
              rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
            }),
          ],
        }),
  });

  const [isLoading, setIsLoading] = useState(true);
  const [serviceAreaProviderMappingArgs, setServiceAreaProviderMappingArgs] = useState(null);
  const [serviceAreaProviderMappingIsAdding, setServiceAreaProviderMappingIsAdding] = useState(
    false,
  );

  const reloadAddressCoverage = useCallback(async () => {
    const [addressCoverage] = await Promise.all([
      dispatch(UtilityProviderActions.getAddressCoverage, { addressId, refresh: true }),
      dispatch(UtilityActivationActions.getTypesList),
    ]);

    if (addressCoverage) {
      await dispatch(UtilityProviderActions.getMappedProvidersInRange, {
        latitude: addressCoverage.latitude,
        longitude: addressCoverage.longitude,
        rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
        refresh: true,
      });
      await dispatch(UtilityProviderActions.getListByAddressId, {
        addressId: addressCoverage.id,
      });
    }
  }, [addressId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsLoading(true);
    reloadAddressCoverage().then(() => {
      setIsLoading(false);
    });
  }, [addressId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateProviderCoveragePoints = ({
    addressId,
    utilityTypeId,
    utilityProviderIds,
    coveragePointsConfig,
    mappingNotes,
  }) => {
    typeCheck('addressId::PositiveNumber', addressId);
    typeCheck('utilityProviderIds::Array', utilityProviderIds);
    typeCheck('utilityTypeId::PositiveNumber', utilityTypeId);
    typeCheck('coveragePointsConfig::Maybe Array', coveragePointsConfig);
    typeCheck('mappingNotes::Maybe String', mappingNotes);

    return dispatch(requestUtilityApi, {
      action: 'SR_API_ACTION_UTILITY_PROVIDER_UPDATE_ADDRESS_COVERAGE',
      payload: {
        addressId,
        utilityTypeId,
        utilityProviderIds,
        coveragePointsConfig,
        mappingNotes,
      },
    })
      .then(reloadAddressCoverage)
      .then(() => {
        if (onUpdate) {
          onUpdate();
        }
      });
  };

  const toggleRequiredType = (utilityTypeId) => {
    const utilityType = utilityTypes.find(({ id }) => id === utilityTypeId);
    const requiredTypes = addressCoverage.requiredTypes.map(({ utilityTypeId }) => utilityTypeId);
    const isRemovingType = requiredTypes.includes(utilityTypeId);

    const newUtilityTypeIds = isRemovingType
      ? lodashWithout([utilityTypeId], requiredTypes)
      : lodashUniq([...requiredTypes, utilityTypeId]);

    const confirmMessage = `Are you sure you want to mark "${utilityType.label}" as ${
      isRemovingType ? 'not ' : ''
    }required?`;

    if (!global.confirm(confirmMessage)) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: 'SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES',
      payload: { addressId: addressCoverage.id, utilityTypeIds: newUtilityTypeIds },
      onError(error) {
        global.alert(error.message);
      },
    })
      .then(reloadAddressCoverage)
      .then(() => {
        if (onUpdate) {
          onUpdate();
        }
      });
  };

  const onMapServiceAreaProvider = ({
    utilityServiceAreaId,
    utilityTypeId,
    companyName,
    addressId,
  }) => {
    setServiceAreaProviderMappingArgs({
      utilityServiceAreaId,
      utilityTypeId,
      companyName,
      addressId,
    });
    setServiceAreaProviderMappingIsAdding(false);
  };

  const onMapServiceAreaProviderSelected = (utilityProvider) => {
    dispatch(requestUtilityApi, {
      action: 'SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING',
      payload: {
        utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
        utilityProviderId: utilityProvider.id,
      },
      onSuccess: () => {
        reloadAddressCoverage();
        onCloseMapServiceAreaProvider();
      },
    });
  };

  const onMapServiceAreaProviderAddNew = () => {
    setServiceAreaProviderMappingIsAdding(true);
  };

  const onMapServiceAreaProviderAddNewSubmit = (utilityProviderUpsertForm) => {
    return dispatch(requestUtilityApi, {
      action: 'SR_API_ACTION_UTILITY_PROVIDER_UPSERT',
      payload: utilityProviderUpsertForm,
    }).then(({ utilityProvider }) => {
      return dispatch(requestUtilityApi, {
        action: 'SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING',
        payload: {
          utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
          utilityProviderId: utilityProvider.id,
        },
        onSuccess: () => {
          reloadAddressCoverage();
          onCloseMapServiceAreaProvider();
        },
      });
    });
  };

  const onConfirmMapping = ({ addressId, utilityTypeId }) => {
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING,
      payload: {
        addressId,
        utilityTypeId,
      },
      onSuccess: () => {
        reloadAddressCoverage().then(() => {
          if (onUpdate) {
            onUpdate();
          }
        });
      },
    });
  };

  const onCloseMapServiceAreaProvider = () => {
    setServiceAreaProviderMappingArgs(null);
    setServiceAreaProviderMappingIsAdding(false);
  };

  if (isLoading) {
    return <div className="loading-ellipses" />;
  }

  if (!addressCoverage) {
    return <div>{`Address ID ${addressId} not found.`}</div>;
  }

  const utilityProvidersByAddressList = utilityProvidersByAddress || [];
  const utilityProvidersInRangeList = utilityProvidersInRange || [];

  const utilityProvidersByTypeId = addressCoverage.items.reduce((memo, item) => {
    const byAddress = utilityProvidersByAddressList.filter(
      ({ utilityTypeId, type }) => utilityTypeId === item.type.id || type?.id === item.type.id,
    );
    const mergedById = {
      ...lodashKeyBy('id', byAddress),
      ...lodashKeyBy('id', item.availableProviders || []),
    };

    memo[item.type.id] = Object.values(mergedById);
    return memo;
  }, {});
  const utilityProvidersInRangeByTypeId = lodashGroupBy(
    'utilityTypeId',
    utilityProvidersInRangeList,
  );
  const utilityProvidersByAddressByTypeId = lodashGroupBy(
    'utilityTypeId',
    utilityProvidersByAddressList,
  );
  return (
    <>
      <SrUtilityProfitAdminProcessAddressForm
        addressCoverage={addressCoverage}
        utilityTypes={utilityTypes}
        utilityProvidersByTypeId={utilityProvidersByTypeId}
        updateProviderCoveragePoints={updateProviderCoveragePoints}
        utilityProvidersInRangeByTypeId={utilityProvidersInRangeByTypeId}
        utilityProvidersByAddressByTypeId={utilityProvidersByAddressByTypeId}
        toggleRequiredType={toggleRequiredType}
        onMapServiceAreaProvider={onMapServiceAreaProvider}
        onConfirmMapping={onConfirmMapping}
        markAsRequiredEnabled={markAsRequiredEnabled}
        onUpdate={onUpdate}
        collapsible
        addressRequiredUtilityTypes={addressRequiredUtilityTypes}
      />
      {serviceAreaProviderMappingArgs && (
        <UPProviderSearchForm
          utilityType={utilityTypes.find(
            ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
          )}
          addressDisplay={serviceAreaProviderMappingArgs.companyName}
          onProviderSelected={onMapServiceAreaProviderSelected}
          onClose={onCloseMapServiceAreaProvider}
          onAddNew={onMapServiceAreaProviderAddNew}
        />
      )}
      {serviceAreaProviderMappingIsAdding && (
        <UPProviderUpsertForm
          title="Connect to a Provider"
          utilityType={utilityTypes.find(
            ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
          )}
          initialValues={{
            addressId: serviceAreaProviderMappingArgs.addressId,
            utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
          }}
          addressDisplay={serviceAreaProviderMappingArgs.companyName}
          onCancel={onCloseMapServiceAreaProvider}
          onSubmit={onMapServiceAreaProviderAddNewSubmit}
        />
      )}
    </>
  );
};

UPAdminProcessAddressFormConnected.propTypes = {
  addressId: PropTypes.number.isRequired,
  markAsRequiredEnabled: PropTypes.bool,
  onUpdate: PropTypes.func,
};

export default UPAdminProcessAddressFormConnected;
